import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Box, Typography, Button, Stack, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import Link from "@shared/components/contents/link/Link";
import Icon from "@shared/components/Icon";
const Container = styled(Box)(({ theme, barColor }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    minHeight: "fit-content",
    backgroundColor: theme.palette.background.paper,
    gap: "20px",
    border: "1px solid ",
    borderTop: "none",
    borderColor: theme.palette.border?.default,
    position: "relative",
    boxSizing: "border-box",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "6px",
        backgroundColor: barColor || theme.palette.border?.active || theme.palette.primary.main,
        zIndex: 2,
    },
}));
const OfferingCard = (props) => {
    const { variant, id, title, surtitle, description, features, hideFeatures, plusFeatureIcon, plusFeature, size = "L", price, priceDetails, button, iconButton, barColor, } = props;
    const isMedium = size === "M";
    const isHighlighted = variant === "highlighted";
    return (_jsxs(Container, { barColor: isHighlighted ? "primary.main" : barColor, sx: {
            padding: isMedium
                ? { xs: "40px 24px 32px 24px", md: "40px 32px" }
                : { xs: 3, md: "31px" },
            height: { sm: "fit-content", lg: "-webkit-fill-available" },
            "@supports (height: stretch)": {
                height: { lg: "stretch" },
            },
            ...(isMedium && {
                gap: 0,
            }),
            ...(!isMedium && {
                minHeight: {
                    lg: "800px",
                },
            }),
            ...(isHighlighted && { backgroundColor: "primary.main" }),
        }, children: [_jsxs(Stack, { spacing: isMedium ? { xs: 2, md: 2.5 } : { xs: 2.5, md: 3 }, children: [(surtitle || title) && (_jsxs(Stack, { spacing: { xs: 1, md: 1.5 }, children: [surtitle && (_jsx(Typography, { variant: "subtitle2", component: "p", sx: {
                                    color: isHighlighted
                                        ? "primary.contrastText"
                                        : "text.primary",
                                    fontSize: { xs: 12, md: 14 },
                                    ...(isMedium && {
                                        fontWeight: 400,
                                        color: "text.secondary",
                                    }),
                                }, children: surtitle })), title && (_jsx(Typography, { variant: "h3", component: "h3", sx: {
                                    color: isHighlighted
                                        ? "primary.contrastText"
                                        : "text.primary",
                                    fontSize: { xs: 20, md: 24 },
                                    letterSpacing: { xs: "-0.02em" },
                                    lineHeight: { xs: "125%", md: "135%" },
                                    ...(isMedium && {
                                        maxWidth: 280,
                                        lineHeight: { xs: "135%" },
                                    }),
                                }, children: title }))] })), description && (_jsx(Typography, { variant: "body1", sx: {
                            color: isMedium ? "text.secondary" : "text.primary",
                            ...(isHighlighted && { color: "primary.contrastText" }),
                            fontSize: { xs: 14, md: 16 },
                            lineHeight: { xs: "150%", md: "160%" },
                        }, children: description })), features && !hideFeatures && (_jsx(Box, { sx: {
                            width: "100%",
                            "& ul": {
                                margin: 0,
                            },
                            "& ul > li:only-child": {
                                p: "8px 12px",
                                textIndent: 0,
                                border: "1px solid",
                                borderColor: "border.grey",
                                backgroundColor: "background.grey",
                                "&::before": {
                                    marginRight: "12px",
                                    verticalAlign: "middle",
                                },
                            },
                            "& ul li::before": {
                                backgroundColor: `${barColor || "#0000FF"} !important`,
                            },
                        }, children: features })), plusFeature && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: { opacity: 0.4 } }), _jsxs(Stack, { direction: "row", spacing: 1.5, sx: { alignItems: { xs: "center", md: "flex-start" } }, children: [plusFeatureIcon?.icon && (_jsx(Icon, { ...plusFeatureIcon, style: {
                                            fontSize: "16px",
                                            minWidth: "18px",
                                            minHeight: "18px",
                                        } })), _jsx(Typography, { variant: "body2", sx: { color: "text.secondary", fontSize: { xs: 12, md: 14 } }, children: plusFeature })] })] }))] }), _jsxs(Stack, { sx: {
                    width: "100%",
                    alignItems: "flex-end",
                    ...(!isMedium && {
                        width: "100%",
                        pt: { xs: 0, md: 4 },
                        alignItems: "initial",
                    }),
                }, spacing: isMedium ? 0 : 3, children: [price && (_jsxs(Box, { children: [_jsx(Typography, { sx: {
                                    fontWeight: { xs: 600, md: 700 },
                                    fontSize: { xs: 20, md: 24 },
                                    lineHeight: 1.35,
                                    letterSpacing: { xs: "0.02em", md: "-0.02em" },
                                    color: "text.primary",
                                }, children: price }), priceDetails && (_jsx(Typography, { variant: "caption", sx: {
                                    display: "block",
                                    lineHeight: "160%",
                                    color: "text.secondary",
                                }, children: priceDetails }))] })), button && (button.page || button.url) && (_jsx(Button, { component: Link, variant: isMedium && !isHighlighted ? "text" : "contained", color: "secondary", fullWidth: !isMedium, ...(iconButton?.icon !== "" && {
                            endIcon: _jsx(Icon, { ...iconButton, style: { fontSize: 20 } }),
                        }), ...button, sx: {
                            textAlign: "center",
                            p: { xs: "14px 16px", md: "16px 20px" },
                            ...(isMedium && {
                                color: "primary.main",
                                fontWeight: 600,
                                fontSize: 16,
                                lineHeight: "150%",
                                letterSpacing: "-0.02em",
                            }),
                            ...(isHighlighted && {
                                color: "primary.main",
                                backgroundColor: "primary.contrastText",
                            }),
                        }, children: button.title || "Découvrir" }))] })] }, id));
};
export default OfferingCard;
